import { Box, Button, Image, Select, VStack } from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { Checkmark } from '../../../../assets/Images';
import DuplicateDealMessage from '../../../../components/DuplicateDealMessage';
import TextWithTooltip from '../../../../components/TextWithTooltip';
import VehicleInfo from '../../../../components/VehicleInfo';
import { Option } from '../../../../components/formComponents/Select';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import { ERROR_TYPES } from '../../../../constants/tempInfos';
import { VEHICLE_COLORS } from '../../../../constants/vehicleColors';
import {
  KbbVinResult,
  MethodFiVehicle,
  useDecodeAffiliateCarDetailsLazyQuery,
  useKbbVinLazyQuery,
  useTemporaryInfoUpdateMutation,
} from '../../../../gql/generated/graphql';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import { TRIM_EXPLANATION } from '../../../../utils/cars';
import { capitalizeWords, replaceUnderscores } from '../../../../utils/strings';
import { allowOldCarAtom } from './MethodFiDevToolbar';

type DecodedCarData = {
  vin: string;
  year: string;
  make: string;
  model: string;
  fuel_type: string;
  vehicle_type: string;
};

type AutoFillCarPanelProps = {
  vehicles: MethodFiVehicle[];
  onNotFound: () => void;
  onComplete: () => void;
  preselectedVin?: string | null;
};

const VEHICLES_PER_PAGE = 3;

const normalizeColor = (methodFiColor: string | null | undefined): string | undefined => {
  if (!methodFiColor) return undefined;

  const colorLower = methodFiColor.toLowerCase();
  const vehicleColor = Object.values(VEHICLE_COLORS).find(
    (vc) => vc.toLowerCase() === colorLower || colorLower.includes(vc.toLowerCase()),
  );

  return vehicleColor;
};

const AutoFillCarPanel = ({
  vehicles,
  onNotFound,
  onComplete,
  preselectedVin,
}: AutoFillCarPanelProps) => {
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const allowOldCar = useAtomValue(allowOldCarAtom);

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation({
    context: { isErrorHandled: true },
  });
  const [decodeCar] = useDecodeAffiliateCarDetailsLazyQuery({ context: { isErrorHandled: true } });
  const [getKbbDetails] = useKbbVinLazyQuery({ context: { isErrorHandled: true } });

  const [selectedVin, setSelectedVin] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(VEHICLES_PER_PAGE);
  const [dealExists, setDealExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Trim selection state
  const [showTrimSelector, setShowTrimSelector] = useState(false);
  const [selectedTrim, setSelectedTrim] = useState('');
  const [trimOptions, setTrimOptions] = useState<Option[]>([]);
  const [decodedCarData, setDecodedCarData] = useState<DecodedCarData | null>(null);
  const [kbbResults, setKbbResults] = useState<KbbVinResult[]>([]);
  const [selectedVehicleColor, setSelectedVehicleColor] = useState<string | null>(null);

  const sortedVehicles = useMemo(
    () =>
      [...vehicles].sort((a, b) => {
        const yearA = parseInt(a.year ?? '0', 10);
        const yearB = parseInt(b.year ?? '0', 10);
        return yearB - yearA;
      }),
    [vehicles],
  );

  // Auto-select preselected VIN or single vehicle
  useEffect(() => {
    if (preselectedVin && sortedVehicles.some((v) => v.vin === preselectedVin)) {
      setSelectedVin(preselectedVin);
    } else if (sortedVehicles.length === 1 && sortedVehicles[0].vin) {
      setSelectedVin(sortedVehicles[0].vin);
    }
  }, [sortedVehicles, preselectedVin]);

  const visibleVehicles = sortedVehicles.slice(0, visibleCount);
  const hasMore = visibleCount < sortedVehicles.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + VEHICLES_PER_PAGE);
  };

  const saveVehicleData = async (
    carData: DecodedCarData,
    kbbVehicle: KbbVinResult | undefined,
    color: string | null | undefined,
  ) => {
    await updateTemporaryInfo({
      variables: {
        info: {
          id: guid,
          data: {
            vin: carData.vin,
            year: carData.year,
            make: carData.make,
            model: carData.model,
            fuel_type: carData.fuel_type,
            vehicle_type: carData.vehicle_type,
            color: normalizeColor(color),
            kbb_vehicle_id: kbbVehicle?.vehicleId,
            kbb_vehicle_name: kbbVehicle?.vehicleName,
            kbb_trim_name: kbbVehicle?.trimName,
            kbb_all_options: kbbVehicle?.vehicleOptions,
          },
        },
      },
    });
  };

  const completeVehicleSelection = async (
    decoded: DecodedCarData,
    results: KbbVinResult[],
    trimName: string,
    color: string | null | undefined,
  ) => {
    const kbbVehicle = results.find((r) => r.trimName === trimName);
    await saveVehicleData(decoded, kbbVehicle, color);
    setIsLoading(false);
    onComplete();
  };

  const handleVehicleConfirm = async (vehicle: MethodFiVehicle) => {
    setIsLoading(true);

    const { data: decodedCar, error } = await decodeCar({
      variables: { vin: vehicle.vin, temp_info_id: guid, ignore_existing_deal: allowOldCar },
    });

    if (error?.message === ERROR_TYPES.DEAL_EXISTS && !allowOldCar) {
      setIsLoading(false);
      setDealExists(true);
      return;
    }

    if (error || !decodedCar?.decodeAffiliateCarDetails?.vin) {
      setIsLoading(false);
      onNotFound();
      return;
    }

    const carDetails = decodedCar.decodeAffiliateCarDetails;

    const decoded: DecodedCarData = {
      vin: carDetails.vin ?? '',
      year: carDetails.year ?? '',
      make: carDetails.make ?? '',
      model: carDetails.model ?? '',
      fuel_type: carDetails.fuel_type ?? '',
      vehicle_type: carDetails.vehicle_type ?? '',
    };

    const { data: kbbVinData } = await getKbbDetails({
      variables: { vin: carDetails.vin as string },
    });

    const vinResults = (kbbVinData?.kbbVin?.vinResults ?? []).filter(
      (r): r is KbbVinResult => r !== null,
    );
    const uniqueTrimNames = Array.from(new Set(vinResults.map((r) => r.trimName ?? '')));
    const options = uniqueTrimNames.map((trimName) => ({ label: trimName, value: trimName }));

    // Try to match MethodFi series to KBB trim
    const matchedTrim = options.find(
      (opt) =>
        opt.value.toLowerCase().includes(vehicle.series?.toLowerCase() ?? '') ||
        vehicle.series?.toLowerCase().includes(opt.value.toLowerCase()),
    );

    const selectedTrimValue = options.length === 1 ? options[0].value : matchedTrim?.value;

    if (selectedTrimValue) {
      await completeVehicleSelection(decoded, vinResults, selectedTrimValue, vehicle.major_color);
    } else {
      // Show trim selector
      setDecodedCarData(decoded);
      setKbbResults(vinResults);
      setTrimOptions(options);
      setSelectedVehicleColor(vehicle.major_color ?? null);
      setShowTrimSelector(true);
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (showTrimSelector) {
      if (selectedTrim && decodedCarData) {
        setIsLoading(true);
        await completeVehicleSelection(
          decodedCarData,
          kbbResults,
          selectedTrim,
          selectedVehicleColor,
        );
      }
      return;
    }

    const vehicle = selectedVin && sortedVehicles.find((v) => v.vin === selectedVin);
    if (!vehicle) return;

    await handleVehicleConfirm(vehicle);
  };

  const isConfirmDisabled = showTrimSelector ? !selectedTrim : !selectedVin;

  return (
    <VStack spacing={4}>
      {showTrimSelector && decodedCarData ? (
        <VStack spacing={4} w="full">
          <VehicleInfo
            year={decodedCarData.year}
            make={decodedCarData.make}
            model={decodedCarData.model}
            vin={decodedCarData.vin}
            infoLoading={false}
          />
          <Select
            placeholder="Select Trim"
            value={selectedTrim}
            onChange={(e) => setSelectedTrim(e.target.value)}
            w="full"
          >
            {trimOptions.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </Select>
          <TextWithTooltip label={TRIM_EXPLANATION}>What is my trim?</TextWithTooltip>
        </VStack>
      ) : (
        <>
          <VStack spacing={3} w="full">
            {visibleVehicles.map((vehicle) => {
              const isSelected = selectedVin === vehicle.vin;
              return (
                <Box
                  key={vehicle.vin}
                  w="full"
                  borderRadius={5}
                  border="2px solid"
                  borderColor={isSelected ? 'blue.500' : 'transparent'}
                  cursor="pointer"
                  onClick={() => setSelectedVin(vehicle.vin ?? null)}
                  _hover={{ borderColor: isSelected ? 'blue.500' : 'gray.300' }}
                  position="relative"
                >
                  <VehicleInfo
                    year={vehicle.year ?? ''}
                    make={capitalizeWords(replaceUnderscores(vehicle.make || ''))}
                    model={capitalizeWords(replaceUnderscores(vehicle.model || ''))}
                    vin={vehicle.vin ?? ''}
                    infoLoading={false}
                    p={3}
                  />
                  {isSelected && (
                    <Image
                      src={Checkmark}
                      alt="Checkmark"
                      position="absolute"
                      top={2}
                      right={2}
                      boxSize={6}
                    />
                  )}
                </Box>
              );
            })}
          </VStack>

          {hasMore && (
            <Button variant="link" onClick={handleLoadMore} size="sm">
              Load More
            </Button>
          )}
        </>
      )}

      <VStack spacing={3} mt={4} w="full">
        <PrimaryButton
          onClick={handleConfirm}
          isDisabled={isConfirmDisabled || dealExists}
          isLoading={isLoading}
          w="full"
        >
          {showTrimSelector ? 'CONFIRM TRIM' : 'YES, THIS IS MY LEASED VEHICLE'}
        </PrimaryButton>
        {dealExists && <DuplicateDealMessage />}
        <Button onClick={onNotFound} variant="link" textAlign="center">
          {showTrimSelector ? "I don't see my trim" : "I don't see my leased vehicle"}
        </Button>
      </VStack>
    </VStack>
  );
};

export default AutoFillCarPanel;
